import React, { useEffect, useRef } from "react";
import { Avatar, Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Shield as ShieldIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { useClinicTheme } from "../../theme";
import { COLORS } from "../../styles/uiConstants";
import EmptyState from "../../components/atoms/EmptyState";
import { ActionButton } from "../../components/atoms";
import {
  ListView,
  ListSkeleton,
  ListPagination,
} from "../../components/organisms/list";
import useAutoPageSize from "../../hooks/useAutoPageSize";
import RoleTemplateCard from "./RoleTemplateCard";

/** Başlıklar literal büyük harf — CSS uppercase Türkçe İ'yi bozar. */
export const TEMPLATE_COLUMNS = [
  { key: "name", label: "ŞABLON" },
  { key: "permissions", label: "İZİN" },
  { key: "code", label: "KOD" },
  { key: "actions", label: "" },
];

export default function RoleTemplatesList({
  templates = [],
  isLoading = false,
  page,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  onCopyCode,
  onCreate,
  canCreate = false,
  canUpdate = false,
  canDelete = false,
  hasFilter = false,
}) {
  const { palette } = useClinicTheme();
  const bodyRef = useRef(null);
  const autoPageSize = useAutoPageSize(bodyRef, { rowHeight: 56, headerHeight: 40 });

  useEffect(() => {
    onPageSizeChange?.(autoPageSize);
  }, [autoPageSize, onPageSizeChange]);

  const columns = [
    {
      key: "name",
      label: "ŞABLON",
      render: (t) => (
        <Box
          component={canUpdate ? "button" : "div"}
          type={canUpdate ? "button" : undefined}
          onClick={canUpdate ? () => onEdit?.(t) : undefined}
          aria-label={canUpdate ? `${t?.name} şablonunu düzenle` : undefined}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: "transparent",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: canUpdate ? "pointer" : "default",
            textAlign: "left",
            font: "inherit",
            "&:hover .templateName": canUpdate
              ? { textDecoration: "underline", color: palette.primary }
              : undefined,
            "&:focus-visible": {
              outline: `2px solid ${alpha(palette.primary, 0.5)}`,
              outlineOffset: 2,
            },
          }}
        >
          <Avatar
            variant="rounded"
            sx={{
              width: 32,
              height: 32,
              bgcolor: alpha(palette.primary, 0.1),
              color: palette.primary,
            }}
          >
            <ShieldIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography
              className="templateName"
              sx={{ fontWeight: 600, fontSize: "0.8125rem", color: COLORS.slate800 }}
              noWrap
            >
              {t?.name}
            </Typography>
            {t?.isDefault ? (
              <Tooltip title="Varsayılan şablon">
                <StarIcon sx={{ fontSize: 14, color: COLORS.warning }} />
              </Tooltip>
            ) : null}
          </Box>
        </Box>
      ),
    },
    {
      key: "permissions",
      label: "İZİN",
      cellSx: { width: 120 },
      render: (t) => (
        <Chip
          size="small"
          label={`${(t?.permissions || []).length} izin`}
          sx={{
            height: 24,
            fontSize: "0.6875rem",
            fontWeight: 700,
            borderRadius: "999px",
            bgcolor: alpha(palette.primary, 0.08),
            color: palette.primary,
          }}
        />
      ),
    },
    {
      key: "code",
      label: "KOD",
      cellSx: { width: 160 },
      render: (t) =>
        t?.code ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontFamily: "'SF Mono', 'Fira Code', monospace",
                fontWeight: 700,
                color: COLORS.slate700,
              }}
            >
              {t.code}
            </Typography>
            <IconButton
              size="small"
              aria-label={`${t.code} kodunu kopyala`}
              onClick={(e) => {
                e.stopPropagation();
                onCopyCode?.(t.code);
              }}
              sx={{ width: 22, height: 22 }}
            >
              <CopyIcon sx={{ fontSize: 13, color: COLORS.slate400 }} />
            </IconButton>
          </Box>
        ) : (
          <Typography sx={{ fontSize: "0.8125rem", color: COLORS.slate400 }}>—</Typography>
        ),
    },
    {
      key: "actions",
      label: "",
      align: "center",
      cellSx: { width: 96 },
      render: (t) => (
        <Box sx={{ display: "inline-flex", gap: 0.5 }}>
          {canUpdate ? (
            <Tooltip title="Düzenle">
              <IconButton
                size="small"
                aria-label={`${t?.name} şablonunu düzenle`}
                onClick={(e) => { e.stopPropagation(); onEdit?.(t); }}
              >
                <EditIcon fontSize="small" sx={{ color: COLORS.slate500 }} />
              </IconButton>
            </Tooltip>
          ) : null}
          {canDelete && !t?.isDefault ? (
            <Tooltip title="Sil">
              <IconButton
                size="small"
                aria-label={`${t?.name} şablonunu sil`}
                onClick={(e) => { e.stopPropagation(); onDelete?.(t); }}
              >
                <DeleteIcon fontSize="small" sx={{ color: COLORS.error }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
  ];

  return (
    <ListView
      bodyRef={bodyRef}
      columns={columns}
      data={templates}
      rowKey="_id"
      rowSx={{ "&:hover": { bgcolor: alpha(palette.primary, 0.05) } }}
      renderMobileCard={(t) => (
        <RoleTemplateCard
          template={t}
          onEdit={onEdit}
          onDelete={onDelete}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      )}
      isLoading={isLoading}
      loadingNode={
        <ListSkeleton columns={TEMPLATE_COLUMNS} rowCount={pageSize} message="Şablonlar yükleniyor..." />
      }
      empty={
        hasFilter ? (
          <EmptyState
            icon={<ShieldIcon />}
            title="Sonuç bulunamadı"
            description="Arama kriterlerinize uygun şablon bulunamadı."
          />
        ) : (
          <EmptyState
            icon={<ShieldIcon />}
            title="Henüz personel şablonu yok"
            description="Admin personeli için ilk şablonu oluşturun."
            action={
              canCreate ? (
                <ActionButton variant="add" palette={palette} onClick={onCreate} label="Yeni Şablon" />
              ) : null
            }
          />
        )
      }
      footer={
        <ListPagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={onPageChange}
        />
      }
    />
  );
}
